import { EntityAudit } from "@modernary/mui/components/audit";
import { EntityDocuments } from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView, type RouterOptions } from "vue-router";

// Troubleshoot your routes using this - https://paths.esm.dev

const names = {
  complaintInvestigations: "ComplaintInvestigation.ComplaintInvestigations",
  list: "ComplaintInvestigation.ComplaintInvestigationList",
  new: "ComplaintInvestigation.ComplaintInvestigationNew",
  newFromComplaint: "ComplaintInvestigation.ComplaintInvestigationNewFromComplaint",
  followupInvestigation: "ComplaintInvestigation.FollowupInvestigation",
  complete: "ComplaintInvestigation.ComplaintInvestigationComplete",
  recomplete: "ComplaintInvestigation.ComplaintInvestigationRecomplete",
  approve: "ComplaintInvestigation.ComplaintInvestigationApprove",
  reject: "ComplaintInvestigation.ComplaintInvestigationReject",
  processMail: "ComplaintInvestigation.ComplaintInvestigationProcessMail",
  base: "ComplaintInvestigation.ComplaintInvestigationBase",
  view: "ComplaintInvestigation.ComplaintInvestigationView",
  edit: "ComplaintInvestigation.ComplaintInvestigationEdit",
  complaints: "ComplaintInvestigation.ComplaintInvestigationComplaints",
  enforcementActions: "ComplaintInvestigation.ComplaintInvestigationEnforcementActions",
  documents: "ComplaintInvestigation.ComplaintInvestigationDocuments",
  documentsEdit: "ComplaintInvestigation.ComplaintInvestigationDocumentsEdit",
  documentsNew: "ComplaintInvestigation.ComplaintInvestigationDocumentsNew",
  investigationNotes: "ComplaintInvestigation.ComplaintInvestigationNotes",
  noteEdit: "ComplaintInvestigation.ComplaintInvestigationNoteEdit",
  noteNew: "ComplaintInvestigation.ComplaintInvestigationNoteNew",
  audit: "ComplaintInvestigation.ComplaintInvestigationAudit",
};

export { names as complaintInvestigationRouteNames };

const complaintInvestigationRoutes: RouterOptions["routes"] = [
  {
    path: "/complaintInvestigation",
    name: names.complaintInvestigations,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: {
          title: "Complaint Investigations",
          lookupGroups: ["ProgramType", "InspectionStatus", "InspectionType", "InspectionComplianceAction"],
        },
        component: () => import("@/views/complaintInvestigations/ComplaintInvestigationList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/complaintInvestigations/ComplaintInvestigationBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          // {
          //   path: "",
          //   name: names.complaintInvestigationNew,
          //   meta: { title: "New Complaint Investigation", isNew: true },
          //   component: () => import("@/views/complaintInvestigations/ComplaintInvestigationEdit.vue"),
          // },
          {
            path: ":complaintId(\\d+)?",
            name: names.newFromComplaint,
            meta: { title: "New Complaint Investigation", isNew: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationEdit.vue"),
          },
          {
            path: "followup/:investigationId(\\d+)?",
            name: names.followupInvestigation,
            meta: { title: "Follow-up Complaint Investigation", isNew: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationEdit.vue"),
          },
        ],
      },

      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/complaintInvestigations/ComplaintInvestigationBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Complaint Investigation View" },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Complaint Investigation Edit", isForm: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationEdit.vue"),
          },
          {
            path: "complete",
            name: names.complete,
            meta: { title: "Complete Investigation", isForm: true },
            component: () => import("@/views/complaintInvestigations/CompleteInvestigation.vue"),
          },
          {
            path: "recomplete",
            name: names.recomplete,
            meta: { title: "Recomplete Investigation", isForm: true },
            component: () => import("@/views/complaintInvestigations/RecompleteInvestigation.vue"),
          },
          {
            path: "approve",
            name: names.approve,
            meta: { title: "Approve Investigation", isForm: true },
            component: () => import("@/views/complaintInvestigations/ApproveInvestigation.vue"),
          },
          {
            path: "reject",
            name: names.reject,
            meta: { title: "Reject Investigation", isForm: true },
            component: () => import("@/views/complaintInvestigations/RejectInvestigation.vue"),
          },
          {
            path: "process",
            name: names.processMail,
            meta: { title: "Process/Mail Investigation", isForm: true },
            component: () => import("@/views/complaintInvestigations/ProcessMailInvestigation.vue"),
          },
          {
            path: "complaints",
            name: names.complaints,
            meta: { title: "Complaint Investigation Complaints" },
            component: () => import("@/views/complaintInvestigations/ComplaintChildList.vue"),
          },
          {
            path: "enforcementActions",
            name: names.enforcementActions,
            meta: { title: "Complaint Enforcement Actions" },
            component: () => import("@/views/complaintInvestigations/EnforcementActionChildList.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Complaint Investigation Documents" },
            component: {
              render: () => (
                <EntityDocuments
                  idField="inspectionId"
                  routeNamePrefix="ComplaintInvestigation.ComplaintInvestigationDocuments"
                />
              ),
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentsEdit,
            meta: { title: "Edit Complaint Investigation Document", isForm: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationDocument.vue"),
          },
          {
            path: "documents/new",
            name: names.documentsNew,
            meta: { title: "Create Complaint Investigation Documents", isForm: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationDocuments.vue"),
          },
          {
            path: "notes",
            name: names.investigationNotes,
            meta: { title: "Complaint Investigation Notes" },
            component: {
              render: () => (
                <EntityNotes
                  idField="inspectionId"
                  routeNamePrefix="ComplaintInvestigation.ComplaintInvestigationNote"
                />
              ),
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Complaint Investigation Note", isForm: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Complaint Investigation Note", isForm: true },
            component: () => import("@/views/complaintInvestigations/ComplaintInvestigationNote.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Complaint Investigation Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="inspection" />,
            },
          },
        ],
      },
    ],
  },
];
export default complaintInvestigationRoutes;
